import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useNavigate, useParams } from "react-router-dom";
import { settingsApi } from "../../api/settings";
import { formatApiError, getApiBaseUrl } from "../../api/client";
import { landingAssetsApi } from "../../api/landingAssets";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import {
  buildProjectSecurePreviewUrl,
  getProjectPublicUrl,
} from "../../utils/projectDeployment";
import { assetValueFromResponse, isManagedLandingAsset } from "../../utils/landingAssets";
import { TRIAL_LANDING_EVENT } from "../../utils/trialOnboarding";
import LandingSectionDialog from "../../components/dashboard/landingEditor/LandingSectionDialog";
import LandingSettingsDialog from "../../components/dashboard/landingEditor/LandingSettingsDialog";
import {
  emptySettings,
  mergeSettings,
} from "../../components/dashboard/landingEditor/config";
import { settingsPayload } from "../../components/dashboard/landingEditor/editorUtils";
import {
  findStepForSection,
  getSiteBuilderProgress,
} from "../../components/dashboard/landingEditor/siteBuilderModel";
import LandingStudioToolbar from "../../components/dashboard/landingStudio/LandingStudioToolbar";
import LandingStudioQuestionDock from "../../components/dashboard/landingStudio/LandingStudioQuestionDock";
import LandingStudioCanvasStage from "../../components/dashboard/landingStudio/LandingStudioCanvasStage";
import LandingCanvas from "../../components/dashboard/landingEditor/LandingCanvas";
import LandingSegmentInspector from "../../components/dashboard/landingStudio/LandingSegmentInspector";
import { resolveNextStudioQuestion } from "../../components/dashboard/landingStudio/landingStudioQuestions";
import {
  setManyValuesAtPaths,
  setValueAtPath,
} from "../../components/dashboard/landingStudio/landingStudioUtils";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

function draftSettingsPayload(form, productsEnabled) {
  const payload = settingsPayload(form, productsEnabled);
  delete payload.publicStatus;
  return payload;
}

function deploymentFromPublishResponse(data = {}) {
  const source = data.deployment || data.deploymentStatus || null;
  const deployment = typeof source === "string" ? { status: source } : source;
  const refreshQueued =
    data.publication?.deploymentQueued ??
    data.socialPreview?.queued ??
    deployment?.refreshQueued;

  if (!deployment && refreshQueued == null) return null;
  return {
    ...(deployment || {}),
    refreshQueued: refreshQueued === true,
  };
}

function openPreviewPlaceholder() {
  const previewWindow = window.open("about:blank", "_blank");
  if (!previewWindow) return null;

  try {
    previewWindow.document.title = "Preparando vista previa segura";
    previewWindow.document.body.textContent = "Preparando la vista previa del borrador…";
    Object.assign(previewWindow.document.body.style, {
      margin: "0",
      minHeight: "100vh",
      display: "grid",
      placeItems: "center",
      padding: "24px",
      boxSizing: "border-box",
      fontFamily: "Montserrat, Arial, sans-serif",
      fontWeight: "700",
      color: "#19242F",
      background: "#F4F5F7",
      textAlign: "center",
    });
    previewWindow.opener = null;
    previewWindow.focus();
  } catch {
    // La navegación final sigue siendo válida aunque el placeholder no se pueda decorar.
  }

  return previewWindow;
}

function collectManagedAssets(value, output = new Set()) {
  if (typeof value === "string") {
    if (isManagedLandingAsset(value)) output.add(value);
    return output;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectManagedAssets(item, output));
    return output;
  }
  if (value && typeof value === "object") {
    Object.values(value).forEach((item) => collectManagedAssets(item, output));
  }
  return output;
}

export default function ProjectLandingVisualEditorPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const mobileViewport = useMediaQuery("(max-width:700px)");
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();

  const [project, setProject] = React.useState(null);
  const [form, setForm] = React.useState(emptySettings);
  const [device, setDevice] = React.useState(() => (mobileViewport ? "mobile" : "desktop"));
  const [activeSection, setActiveSection] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [savingPath, setSavingPath] = React.useState("");
  const [statusTransition, setStatusTransition] = React.useState("");
  const [previewing, setPreviewing] = React.useState(false);
  const [openingPreview, setOpeningPreview] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const formRef = React.useRef(emptySettings);
  const dirtyRef = React.useRef(false);
  const pendingAssetDeletesRef = React.useRef(new Set());
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = React.useState(0);
  const [sectionOpen, setSectionOpen] = React.useState(false);
  const [sectionId, setSectionId] = React.useState("hero");
  const [segmentInspectorOpen, setSegmentInspectorOpen] = React.useState(false);
  const [segmentInspectorTool, setSegmentInspectorTool] = React.useState("content");
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId || item._id === projectId) || activeProject,
    [activeProject, projectId, projects]
  );
  const effectiveProject = project || contextProject;
  const landingEnabled = isProjectModuleEnabled(effectiveProject, "landing");
  const productsEnabled = isProjectModuleEnabled(effectiveProject, "products");
  const publicUrl = getProjectPublicUrl(effectiveProject);
  const progress = React.useMemo(
    () => getSiteBuilderProgress(form, productsEnabled),
    [form, productsEnabled]
  );
  const question = React.useMemo(
    () => resolveNextStudioQuestion(progress),
    [progress]
  );
  const saving = Boolean(savingPath);
  const publishing = statusTransition === "published";
  const changingPublicStatus = Boolean(statusTransition);

  React.useEffect(() => {
    formRef.current = form;
  }, [form]);

  React.useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  React.useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirty]);

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [loadProjects, projects.length]);

  React.useEffect(() => {
    let active = true;
    projectsApi
      .get(projectId)
      .then((data) => {
        if (!active) return;
        const nextProject = data.project || null;
        setProject(nextProject);
        if (nextProject) setActiveProject(nextProject);
      })
      .catch(() => null);

    return () => {
      active = false;
    };
  }, [projectId, setActiveProject]);

  React.useEffect(() => {
    let active = true;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const data = await settingsApi.get(projectId);
        if (!active) return;
        const merged = mergeSettings(data.settings);
        formRef.current = merged;
        setForm(merged);
        dirtyRef.current = false;
        setDirty(false);

        const initialProgress = getSiteBuilderProgress(merged, productsEnabled);
        const firstIncomplete = initialProgress.steps.find((step) => !step.done);
        const firstSection = firstIncomplete?.actions?.find((action) => action.type === "section")?.section;
        setActiveSection(firstSection || "hero");

        window.dispatchEvent(
          new CustomEvent(TRIAL_LANDING_EVENT, {
            detail: { projectId, settings: merged },
          })
        );
      } catch (err) {
        if (active) setError(err.message || "No se pudo cargar la configuración de la página");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [productsEnabled, projectId]);

  const stageDraft = React.useCallback(
    (nextForm) => {
      const previousAssets = collectManagedAssets(formRef.current);
      const nextAssets = collectManagedAssets(nextForm);
      previousAssets.forEach((path) => {
        if (!nextAssets.has(path)) pendingAssetDeletesRef.current.add(path);
      });
      nextAssets.forEach((path) => pendingAssetDeletesRef.current.delete(path));

      formRef.current = nextForm;
      setForm(nextForm);
      dirtyRef.current = true;
      setDirty(true);
      window.dispatchEvent(
        new CustomEvent(TRIAL_LANDING_EVENT, {
          detail: { projectId, settings: nextForm },
        })
      );
      return Promise.resolve(nextForm);
    },
    [projectId]
  );

  const saveDraft = React.useCallback(async () => {
    const snapshot = formRef.current;
    const pendingDeletesAtStart = new Set(pendingAssetDeletesRef.current);
    try {
      setSavingPath("draft");
      setError("");
      const data = await settingsApi.update(
        projectId,
        draftSettingsPayload(snapshot, productsEnabled)
      );
      const merged = mergeSettings(data.settings);
      const changedDuringSave = formRef.current !== snapshot;
      const nextForm = changedDuringSave
        ? {
            ...formRef.current,
            revision: merged.revision,
            draftRevision: merged.draftRevision,
            publishedRevision: merged.publishedRevision,
            publicStatus: merged.publicStatus,
          }
        : merged;
      formRef.current = nextForm;
      setForm(nextForm);
      dirtyRef.current = changedDuringSave;
      setDirty(changedDuringSave);

      const currentAssets = collectManagedAssets(formRef.current);
      const pendingDeletes = Array.from(pendingDeletesAtStart).filter(
        (path) => isManagedLandingAsset(path) && !currentAssets.has(path)
      );
      pendingDeletesAtStart.forEach((path) => {
        if (!currentAssets.has(path)) pendingAssetDeletesRef.current.delete(path);
      });
      await Promise.allSettled(
        pendingDeletes.map((path) => landingAssetsApi.remove(projectId, path))
      );

      window.dispatchEvent(
        new CustomEvent(TRIAL_LANDING_EVENT, {
          detail: { projectId, settings: nextForm },
        })
      );
      setMessage(
        changedDuringSave
          ? "Borrador guardado. Hay cambios nuevos todavía pendientes."
          : "Borrador guardado. La versión publicada no cambió."
      );
      return nextForm;
    } catch (err) {
      dirtyRef.current = true;
      setDirty(true);
      setError(err.message || "No se pudo guardar el borrador");
      return null;
    } finally {
      setSavingPath("");
    }
  }, [productsEnabled, projectId]);

  const saveSettingsDialog = React.useCallback(
    async (nextForm) => {
      await stageDraft(nextForm);
      const saved = await saveDraft();
      if (!saved) {
        throw new Error("No se pudieron guardar los ajustes del borrador.");
      }
      return saved;
    },
    [saveDraft, stageDraft]
  );

  const commitPath = React.useCallback(
    (path, value) => {
      const next = setValueAtPath(formRef.current, path, value);
      return stageDraft(next);
    },
    [stageDraft]
  );

  const commitMany = React.useCallback(
    (changes) => {
      const next = setManyValuesAtPaths(formRef.current, changes);
      return stageDraft(next);
    },
    [stageDraft]
  );

  const uploadAssetFile = React.useCallback(
    async (file, metadata = {}) => {
      const response = await landingAssetsApi.upload(projectId, file, {
        pageKey: "home",
        ...metadata,
      });
      const value = assetValueFromResponse(response);
      if (!value) throw new Error("El servidor no devolvió la ruta del archivo");
      return { ...response.asset, value };
    },
    [projectId]
  );

  const deferAssetDeletion = React.useCallback(
    async () => ({ ok: true, removed: false, pending: true }),
    []
  );

  const uploadInlineImage = React.useCallback(
    async (path, file, metadata = {}) => {
      try {
        setSavingPath(`asset:${path}`);
        const asset = await uploadAssetFile(file, metadata);
        const next = setValueAtPath(formRef.current, path, asset.value);
        return await stageDraft(next);
      } catch (err) {
        setError(err.message || "No se pudo subir la imagen");
        throw err;
      } finally {
        setSavingPath("");
      }
    },
    [stageDraft, uploadAssetFile]
  );

  const removeInlineImage = React.useCallback(
    async (path) => {
      const current = formRef.current;

      try {
        const next = setValueAtPath(current, path, "");
        return await stageDraft(next);
      } catch (err) {
        setError(err.message || "No se pudo preparar la eliminación de la imagen");
        throw err;
      }
    },
    [stageDraft]
  );

  const openSettingsAt = React.useCallback((tab = 0) => {
    setSettingsInitialTab(tab);
    setSettingsOpen(true);
  }, []);

  const openSection = React.useCallback((nextSection) => {
    setActiveSection(nextSection);
    setSectionId(nextSection);
    setSectionOpen(true);
  }, []);

  const runAction = React.useCallback(
    (action) => {
      if (!action) return;
      if (action.type === "section" && action.section) {
        openSection(action.section);
        return;
      }
      openSettingsAt(Number(action.tab || 0));
    },
    [openSection, openSettingsAt]
  );

  const selectProgressStep = React.useCallback(
    (step) => {
      const firstSection = step?.actions?.find((action) => action.type === "section");
      runAction(firstSection || step?.actions?.[0]);
    },
    [runAction]
  );

  const selectCanvasSection = React.useCallback(
    (nextSection) => {
      const relatedStep = findStepForSection(nextSection, productsEnabled);
      if (relatedStep || nextSection) setActiveSection(nextSection);
    },
    [productsEnabled]
  );

  const openSegmentTool = React.useCallback((nextSection, tool = "content") => {
    setActiveSection(nextSection);
    setSectionId(nextSection);
    setSegmentInspectorTool(tool);
    setSegmentInspectorOpen(true);
  }, []);

  const openSegmentTexts = React.useCallback((nextSection) => {
    setSegmentInspectorOpen(false);
    openSection(nextSection);
  }, [openSection]);

  const togglePreview = React.useCallback(() => {
    setSettingsOpen(false);
    setSectionOpen(false);
    setSegmentInspectorOpen(false);
    setActiveSection("");
    setPreviewing((current) => !current);
  }, []);

  const openSecurePreview = React.useCallback(async () => {
    if (!publicUrl) {
      setError("La página pública todavía no tiene una URL disponible para la vista previa.");
      return null;
    }

    const previewWindow = openPreviewPlaceholder();
    if (!previewWindow) {
      setError("El navegador bloqueó la nueva pestaña. Habilitá las ventanas emergentes e intentá nuevamente.");
      return null;
    }

    try {
      setOpeningPreview(true);
      setError("");

      if (dirty) {
        const savedDraft = await saveDraft();
        if (!savedDraft) {
          previewWindow.close();
          return null;
        }
      }

      const data = await settingsApi.createPreviewToken(projectId);
      const preview = data?.preview || null;
      const previewUrl = buildProjectSecurePreviewUrl(
        effectiveProject,
        preview,
        getApiBaseUrl()
      );

      if (!previewUrl) {
        throw new Error("El servidor no devolvió una vista previa válida para esta página.");
      }
      if (previewWindow.closed) {
        throw new Error("La pestaña de vista previa se cerró antes de completar la apertura.");
      }

      previewWindow.location.replace(previewUrl);
      setMessage(
        `Vista previa segura abierta para el borrador v${preview?.draftRevision || formRef.current?.draftRevision || 1}.`
      );
      return preview;
    } catch (err) {
      if (!previewWindow.closed) previewWindow.close();
      setError(err.message || "No se pudo abrir la vista previa segura");
      return null;
    } finally {
      setOpeningPreview(false);
    }
  }, [dirty, effectiveProject, projectId, publicUrl, saveDraft]);

  const applyPublicStatus = React.useCallback(
    async (targetStatus) => {
      const publicStatus = String(targetStatus || "").trim().toLowerCase();
      if (!["draft", "published", "maintenance"].includes(publicStatus)) {
        setError("El estado público seleccionado no es válido.");
        return null;
      }
      if (statusTransition) return null;

      if (dirtyRef.current) {
        const savedDraft = await saveDraft();
        if (!savedDraft) return null;
      }

      try {
        setStatusTransition(publicStatus);
        setError("");
        const data = await settingsApi.setPublicStatus(projectId, publicStatus);
        const publication = data.publication || {};
        const returnedSettings = data.settings || data.publishedSettings || null;
        const base = returnedSettings
          ? mergeSettings(returnedSettings)
          : formRef.current;
        const revision = Number(
          data.revision ?? publication.revision ?? returnedSettings?.revision ?? base.revision ?? 1
        );
        const draftRevision = Number(
          data.draftRevision ??
            publication.draftRevision ??
            returnedSettings?.draftRevision ??
            base.draftRevision ??
            revision
        );
        const publishedRevision = Number(
          data.publishedRevision ??
            publication.publishedRevision ??
            returnedSettings?.publishedRevision ??
            base.publishedRevision ??
            0
        );
        const nextForm = {
          ...base,
          revision,
          draftRevision,
          publicStatus:
            data.publicStatus ||
            publication.publicStatus ||
            returnedSettings?.publicStatus ||
            publicStatus,
          publishedRevision,
        };
        formRef.current = nextForm;
        setForm(nextForm);
        dirtyRef.current = false;
        setDirty(false);

        const deployment = deploymentFromPublishResponse(data);
        const returnedProject = data.project || null;
        if (returnedProject || deployment) {
          const nextProject = {
            ...(effectiveProject || {}),
            ...(returnedProject || {}),
            deployment: {
              ...(effectiveProject?.deployment || {}),
              ...(returnedProject?.deployment || {}),
              ...(deployment || {}),
            },
          };
          setProject(nextProject);
          setActiveProject(nextProject);
        }

        window.dispatchEvent(
          new CustomEvent(TRIAL_LANDING_EVENT, {
            detail: { projectId, settings: nextForm },
          })
        );

        if (nextForm.publicStatus === "published") {
          const deploymentStatus = String(deployment?.status || "").trim();
          const deploymentQueued = deployment?.refreshQueued === true;
          setMessage(
            deploymentStatus === "live" && !deploymentQueued
              ? `Sitio publicado en la revisión ${publishedRevision}.`
              : deploymentQueued
                ? `Revisión ${publishedRevision} publicada. La actualización del deployment quedó en cola.`
                : `Revisión ${publishedRevision} publicada. El deployment quedó ${deploymentStatus || "sin cambios"}.`
          );
        } else if (nextForm.publicStatus === "maintenance") {
          setMessage(
            `Modo mantenimiento activado. La publicación ${publishedRevision || "anterior"} se conservó.`
          );
        } else {
          setMessage(
            "Sitio oculto como borrador. La última publicación y el historial se conservaron."
          );
        }
        return data;
      } catch (err) {
        setError(formatApiError(err, "No se pudo cambiar el estado público del sitio"));
        return null;
      } finally {
        setStatusTransition("");
      }
    },
    [effectiveProject, projectId, saveDraft, setActiveProject, statusTransition]
  );

  const publishDraft = React.useCallback(async () => {
    if (dirtyRef.current) {
      setError("Guardá el borrador antes de publicarlo.");
      return null;
    }
    return applyPublicStatus("published");
  }, [applyPublicStatus]);

  const leaveEditor = () => {
    if (dirty && !window.confirm("Hay cambios sin guardar. ¿Querés salir del editor igualmente?")) {
      return;
    }
    navigate(`/dashboard/proyectos/${projectId}/configuracion/general`);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#F4F5F7" }}>
        <Stack spacing={1.3} alignItems="center">
          <CircularProgress sx={{ color: BRAND.orange }} />
          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
            Preparando la página editable…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (effectiveProject && !landingEnabled) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 2, bgcolor: "#F4F5F7" }}>
        <Stack spacing={1.5} sx={{ maxWidth: 520, textAlign: "center" }}>
          <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.dark }}>
            La página web no está habilitada
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,.62)" }}>
            Este proyecto todavía no tiene habilitado el módulo de landing.
          </Typography>
          <Button startIcon={<ArrowBackRoundedIcon />} onClick={leaveEditor} sx={{ alignSelf: "center" }}>
            Volver al panel
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F4F5F7" }}>
      <LandingStudioToolbar
        title={form.business?.name || effectiveProject?.name || "Tu página web"}
        publicStatus={form.publicStatus}
        revision={form.revision}
        draftRevision={form.draftRevision}
        publishedRevision={form.publishedRevision}
        deploymentStatus={effectiveProject?.deployment?.status}
        device={device}
        saving={saving}
        publishing={publishing}
        statusChanging={changingPublicStatus}
        previewing={previewing}
        openingPreview={openingPreview}
        hasUnsavedChanges={dirty}
        canOpenPublic={Boolean(publicUrl)}
        designTokens={form.designTokens}
        theme={form.theme}
        onBack={leaveEditor}
        onChangeDevice={setDevice}
        onOpenSettings={() => openSettingsAt(0)}
        onChangeDesignTokens={(nextTokens) => commitPath("designTokens", nextTokens)}
        onSaveDraft={saveDraft}
        onTogglePreview={togglePreview}
        onOpenSecurePreview={openSecurePreview}
        onPublish={publishDraft}
        onOpenPublic={() => window.open(publicUrl, "_blank", "noopener,noreferrer")}
      />

      {previewing ? (
        <Box
          component="main"
          sx={{
            position: "fixed",
            inset: "64px 0 0",
            overflow: "auto",
            bgcolor: device === "mobile" ? "#E8EBEF" : "#F4F5F7",
          }}
        >
          <Box
            sx={{
              width: "100%",
              minHeight: "100%",
              py: device === "mobile" ? 2.5 : 0,
              px: device === "mobile" ? 1.5 : 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
            }}
          >
            <LandingCanvas
              form={form}
              device={device}
              productsEnabled={productsEnabled}
              activeSection=""
              studioMode={false}
            />
          </Box>
        </Box>
      ) : (
        <>
          <LandingStudioCanvasStage
            form={form}
            device={device}
            productsEnabled={productsEnabled}
            activeSection={activeSection}
            saving={saving}
            onSelectSection={selectCanvasSection}
            onCommit={commitPath}
            onCommitMany={commitMany}
            onUploadImage={uploadInlineImage}
            onRemoveImage={removeInlineImage}
            onOpenSegmentTool={openSegmentTool}
          />

          <LandingStudioQuestionDock
            question={question}
            progress={progress}
            onResolve={() => runAction(question.action)}
            onSelectStep={selectProgressStep}
          />
        </>
      )}

      {!previewing ? (
        <LandingSegmentInspector
          open={segmentInspectorOpen}
          sectionId={sectionId}
          initialTool={segmentInspectorTool}
          form={form}
          productsEnabled={productsEnabled}
          saving={saving}
          onClose={() => setSegmentInspectorOpen(false)}
          onCommit={commitPath}
          onCommitMany={commitMany}
          onOpenTexts={openSegmentTexts}
          onUploadImage={uploadInlineImage}
          onRemoveImage={removeInlineImage}
        />
      ) : null}

      {!previewing ? (
        <LandingSectionDialog
          open={sectionOpen}
          activeSection={sectionId}
          form={form}
          productsEnabled={productsEnabled}
          saving={savingPath.startsWith("section:")}
          onClose={() => setSectionOpen(false)}
          onSave={(nextForm) => stageDraft(nextForm)}
          onOpenSettings={openSettingsAt}
        />
      ) : null}

      {!previewing ? (
        <LandingSettingsDialog
          open={settingsOpen}
          form={form}
          productsEnabled={productsEnabled}
          saving={savingPath === "settings"}
          changingPublicStatus={changingPublicStatus}
          initialTab={settingsInitialTab}
          showTrialGuide={!progress.complete}
          onClose={() => setSettingsOpen(false)}
          onSave={saveSettingsDialog}
          onChangePublicStatus={applyPublicStatus}
          onUploadAsset={uploadAssetFile}
          onDeleteAsset={deferAssetDeletion}
        />
      ) : null}

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={2200}
        onClose={() => setMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setMessage("")} sx={{ fontWeight: 850 }}>
          {message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5200}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError("")} sx={{ fontWeight: 850 }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
